import { Component, OnInit, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import * as Sentry from '@sentry/angular';
import { AuthService } from '../../services/auth.service';
import { UserActions } from '../../store';
import { ExternalApiService, FeatureService, NavigationService, ProfileService, StorageService } from '../../services';
import { OUTLAW_COOKIES, OUTLAW_QUERY_PARAMS } from '../../consts';
import { PassportUser, User } from '../../types';

@Component({
  selector: 'ol-auto-sign-in',
  template: '<p>redirecting...</p>',
})
export class AutoSignInComponent implements OnInit {
  private readonly profileService = inject(ProfileService);
  private readonly authService = inject(AuthService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private navigationService = inject(NavigationService);
  private store = inject(Store);
  private featureService = inject(FeatureService);
  private storageService = inject(StorageService);
  private externalApiService = inject(ExternalApiService);

  private userToken: string;

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.userToken = this.activatedRoute.snapshot.queryParams[OUTLAW_QUERY_PARAMS.token];

    const backwardsUrl = this.activatedRoute.snapshot.queryParams[OUTLAW_QUERY_PARAMS.backwardsUrl];

    if (backwardsUrl) {
      this.saveBackwardsUrl(backwardsUrl);
    }

    if (this.userToken) {
      this.profileService.updateUserToken(this.userToken);
    } else {
      this.userToken = this.profileService.getUserToken();
    }

    this.redirect().pipe(takeUntil(this.destroy$)).subscribe();
  }

  private redirect(): Observable<boolean> {
    const passportFlow = this.featureService.isPassportFlowEnabled();

    const userRequest = passportFlow ? this.authService.getPassportUserData() : this.authService.getUserData();

    return (userRequest as Observable<User | PassportUser>).pipe(
      switchMap(user => {
        return this.externalApiService.synchronizeExternalServices().pipe(map(() => user));
      }),
      tap({
        next: user => {
          this.store.dispatch(
            UserActions.addUser({
              tokenResponse: { user, token: this.userToken },
            })
          );
        },
        error: error => Sentry.captureException('error with user data', error),
      }),
      switchMap(() => this.navigationService.redirectToFilmOrPlaylist())
    );
  }

  private saveBackwardsUrl(backwardsUrl: string): void {
    this.storageService.set(OUTLAW_COOKIES.return_url, backwardsUrl);
  }
}
